import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * hallmark critique · OnboardingHint
 * tokens locked from docs/design.md §1
 * states covered: hidden (dismissed) · visible (first-visit)
 * mobile verified: 320 / 375 / 414 / 768
 * no fake chrome · no italic headers · single clay accent per viewport
 *
 * Phase 14e (part 2). One-shot floating tip card that appears the first
 * time a user opens the dashboard. Explains the three headline features
 * that aren't obvious from the map alone: click a bus, scrub time, open
 * the Docs tab.
 *
 * Persistence: localStorage flag `bb-onboarding-dismissed` — once the
 * user closes it, it never comes back. Reset by clearing site data.
 */
import { useEffect, useState } from "react";
import { colors } from "../../lib/tokens";
const LS_KEY = "bb-onboarding-dismissed";
export function OnboardingHint() {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        try {
            const dismissed = window.localStorage.getItem(LS_KEY);
            if (!dismissed) {
                // Delay a beat so the map renders first — the hint should reveal AFTER
                // the user sees the interface, not before.
                const t = window.setTimeout(() => setVisible(true), 900);
                return () => window.clearTimeout(t);
            }
        }
        catch {
            /* localStorage disabled → don't show */
        }
        return undefined;
    }, []);
    const dismiss = () => {
        try {
            window.localStorage.setItem(LS_KEY, "1");
        }
        catch {
            /* ignore */
        }
        setVisible(false);
    };
    if (!visible)
        return null;
    return (_jsxs("div", { "data-testid": "onboarding-hint", role: "dialog", "aria-label": "Getting started", style: {
            position: "absolute",
            top: 78,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 900,
            maxWidth: 480,
            width: "calc(100% - 40px)",
            padding: 14,
            borderRadius: 8,
            background: "rgba(0,13,16,0.94)",
            color: colors.pureWhite,
            boxShadow: "0 8px 28px rgba(0,13,16,0.25)",
            fontFamily: "var(--font-display)",
            border: `1px solid ${colors.clayEmber}`,
        }, children: [_jsxs("div", { style: {
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: 8,
                }, children: [_jsxs("div", { children: [_jsx("div", { style: {
                                    fontSize: 10,
                                    letterSpacing: "0.16em",
                                    textTransform: "uppercase",
                                    color: colors.clayEmber,
                                    marginBottom: 2,
                                }, children: "\uD83D\uDC4B Welcome \u2014 three things to try" }), _jsx("div", { style: { fontSize: 16, fontWeight: 700, lineHeight: 1.25 }, children: "The dashboard has more than the map." })] }), _jsx("button", { type: "button", onClick: dismiss, "aria-label": "Dismiss onboarding hint", style: {
                            width: 26,
                            height: 26,
                            border: `1px solid rgba(255,255,255,0.3)`,
                            background: "transparent",
                            color: colors.pureWhite,
                            borderRadius: 1000,
                            cursor: "pointer",
                            fontSize: 14,
                            fontWeight: 700,
                            lineHeight: 1,
                            flexShrink: 0,
                        }, children: "\u00D7" })] }), _jsxs("ol", { style: {
                    margin: 0,
                    padding: 0,
                    listStyle: "none",
                    display: "grid",
                    gap: 8,
                }, children: [_jsx(Hint, { n: 1, title: "Click a bus", body: "Every bus icon is clickable. You'll see the full route, next stop, live occupancy, and the bunching partner (if any). Every other bus fades so your eye tracks one." }), _jsx(Hint, { n: 2, title: "Scrub the time", body: "Bottom-centre of the map has a video-player. Back 10/60s, forward 10/60s, speed control from -8\u00D7 to 8\u00D7, LIVE snap. Every timeline query is deterministic \u2014 same at_ts = same map." }), _jsx(Hint, { n: 3, title: "Open the Docs tab", body: "Top-right nav \u2192 Docs. Interactive playground for POST /api/companion/recommendation and GET /api/mumbai/routes/{route_id}, plus links to Swagger and the MCP info page." })] }), _jsx("div", { style: {
                    marginTop: 10,
                    paddingTop: 8,
                    borderTop: `1px solid rgba(255,255,255,0.15)`,
                    fontSize: 10,
                    color: colors.pebble,
                    letterSpacing: "0.02em",
                }, children: "This hint appears once. Close it and it stays gone." })] }));
}
function Hint({ n, title, body, }) {
    return (_jsxs("li", { style: {
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
        }, children: [_jsx("div", { "aria-hidden": true, style: {
                    width: 22,
                    height: 22,
                    borderRadius: 1000,
                    background: colors.clayEmber,
                    color: colors.pureWhite,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                }, children: n }), _jsxs("div", { children: [_jsx("div", { style: { fontSize: 13, fontWeight: 700, color: colors.pureWhite }, children: title }), _jsx("div", { style: { fontSize: 11.5, color: colors.pebble, lineHeight: 1.35, marginTop: 1 }, children: body })] })] }));
}
